import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '../api/chat';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../components/Toast';
import ChatInbox from '../components/ChatInbox';
import type { ChatAgent, ChatRolloutStatus, ChatSettings } from '../types';

/**
 * The per-franchise chat section. Everything that makes one location's bot
 * different from another's lives here: whether it is switched on, the roster of
 * people who get the leads, their roles and photos, and the one piece of copy
 * that is per-location (the caregiver application link).
 *
 * The conversation flow itself is identical network-wide and is not editable —
 * see Chatbot-Flow-Spec.md.
 */

const STATUS_LABEL: Record<ChatRolloutStatus, string> = {
  not_started: 'Not started',
  ready: 'Ready to install',
  pilot: 'Pilot',
  live: 'Live',
  paused: 'Paused',
};

const STATUS_HELP: Record<ChatRolloutStatus, string> = {
  not_started: 'Nothing set up yet.',
  ready: 'Configured here, but the code is not on the website yet.',
  pilot: 'On the website and being watched closely.',
  live: 'Fully switched over — Digital Air Strike removed.',
  paused: 'Temporarily switched off.',
};

function fmtWhen(iso: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function ChatTab({ af }: { af: string }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin';
  const settingsQ = useQuery({
    queryKey: ['chat-settings', af],
    queryFn: () => chatApi.getSettings(af),
  });
  const agentsQ = useQuery({
    queryKey: ['chat-agents', af],
    queryFn: () => chatApi.listAgents(af),
  });
  const usersQ = useQuery({
    queryKey: ['chat-eligible-users', af],
    queryFn: () =>
      fetch(`/api/chat/eligible-users?af=${encodeURIComponent(af)}`, { credentials: 'include' })
        .then((r) => r.json() as Promise<{ users: Array<{ id: number; name: string | null; email: string | null }> }>),
  });

  const [form, setForm] = useState<Partial<ChatSettings>>({});
  const [saving, setSaving] = useState(false);

  const settings = settingsQ.data?.settings;
  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  async function save() {
    setSaving(true);
    try {
      await chatApi.saveSettings(af, form);
      await qc.invalidateQueries({ queryKey: ['chat-settings', af] });
      showToast('Chat settings saved.');
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (settingsQ.isLoading) return <p className="muted">Loading chat settings…</p>;
  if (settingsQ.error) {
    return (
      <p className="muted">
        Chat isn’t provisioned yet for this dashboard. {(settingsQ.error as Error).message}
      </p>
    );
  }

  const agents = agentsQ.data?.agents.filter((a) => a.active) ?? [];
  const seen = fmtWhen(form.embed_last_seen_at ?? null);

  return (
    <div className="chat-tab" style={{ maxWidth: 940 }}>
      {!!form.enabled && (
        <section style={card}>
          <h3 style={h3}>Conversations</h3>
          <p style={muted}>
            Live chats from this location’s website. Reply to join one — the visitor sees you’ve
            stepped in. Transcripts are kept in Amada’s secure store, not on this dashboard.
          </p>
          <ChatInbox af={af} />
        </section>
      )}

      {/* Website chat configuration is marketing's, not the franchise's.
          Whether chat runs at all, the test-location flag, the backstop
          address and the fallback copy stay with us — a franchisee switching
          their own chat on is the decision this gate exists to prevent. The
          server enforces it too (PUT /settings is adminOnly); this only hides
          the controls. */}
      {isAdmin && (
      <section style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={h3}>Website chat</h3>
            <p style={muted}>
              The chat assistant on this franchise’s website. It answers visitors, captures leads,
              and hands off to a real person when someone here joins the conversation.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              />
              Chat is on
            </label>
            {me?.role === 'admin' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...muted }}>
                <input
                  type="checkbox"
                  checked={!!form.is_test}
                  onChange={(e) => setForm({ ...form, is_test: e.target.checked })}
                />
                Test location
              </label>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 14 }}>
          <Field label="Rollout status" hint={STATUS_HELP[(form.rollout_status ?? 'not_started') as ChatRolloutStatus]}>
            <select
              value={form.rollout_status ?? 'not_started'}
              onChange={(e) => setForm({ ...form, rollout_status: e.target.value as ChatRolloutStatus })}
              style={input}
            >
              {(Object.keys(STATUS_LABEL) as ChatRolloutStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Seen on the website"
            hint="Stamped automatically the last time the chat code loaded on this site."
          >
            <p style={{ margin: '6px 0 0', fontWeight: 600 }}>
              {seen ? seen : <span style={{ color: '#b45309' }}>Not seen yet</span>}
            </p>
          </Field>
        </div>
      </section>
      )}

      <section style={card}>
        <h3 style={h3}>Who gets the leads</h3>
        <p style={muted}>
          Someone <strong>looking for care</strong> or asking about an <strong>LTCi policy review</strong>{' '}
          goes to <strong>sales</strong>. Someone <strong>seeking a job</strong> goes to{' '}
          <strong>recruiting</strong>. One person can hold both.
        </p>

        {agents.length === 0 && (
          <p style={{ ...muted, marginTop: 12 }}>
            Nobody is set up yet — add the people who should receive chats.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {agents.map((a) => (
            <AgentRow key={a.id} af={af} agent={a} onChanged={() => qc.invalidateQueries({ queryKey: ['chat-agents', af] })} />
          ))}
        </div>

        <AddAgent
          af={af}
          users={usersQ.data?.users ?? []}
          existing={agents.map((a) => a.user_id)}
          onAdded={() => qc.invalidateQueries({ queryKey: ['chat-agents', af] })}
        />
      </section>

      <section style={card}>
        <h3 style={h3}>Location details</h3>
        <Field
          label="Caregiver application link"
          hint="Shown to job seekers. Leave blank and the chat simply won’t mention an application."
        >
          <input
            type="url"
            value={form.caregiver_app_link ?? ''}
            placeholder="https://…"
            onChange={(e) => setForm({ ...form, caregiver_app_link: e.target.value })}
            style={input}
          />
        </Field>
        <Field
          label="Backup email for leads"
          hint="Every lead also goes here, so one is never missed if nobody above is available."
        >
          <input
            type="email"
            value={form.backstop_email ?? ''}
            onChange={(e) => setForm({ ...form, backstop_email: e.target.value })}
            style={input}
          />
        </Field>
        <Field
          label="If nobody joins the chat within"
          hint="How long the visitor waits before the assistant reassures them somebody will follow up."
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min={1}
              max={60}
              value={form.fallback_minutes ?? 3}
              onChange={(e) => setForm({ ...form, fallback_minutes: Number(e.target.value) })}
              style={{ ...input, width: 90 }}
            />
            <span style={muted}>minutes</span>
          </div>
        </Field>
        <Field label="What the assistant says then">
          <textarea
            rows={2}
            value={form.fallback_message ?? ''}
            onChange={(e) => setForm({ ...form, fallback_message: e.target.value })}
            style={{ ...input, width: '100%' }}
          />
        </Field>
      </section>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn" onClick={save} disabled={saving} style={primaryBtn}>
          {saving ? 'Saving…' : 'Save chat settings'}
        </button>
        <span style={muted}>Changes take effect on the website within a minute.</span>
      </div>
    </div>
  );
}

function AgentRow({ af, agent, onChanged }: { af: string; agent: ChatAgent; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');

  async function startVerify() {
    setBusy(true);
    try {
      const r = await chatApi.startCellVerification(af, agent.id);
      setVerifying(true);
      showToast(`Code sent to ${agent.cell_e164}. It expires in ${r.expires_in_minutes} minutes.`);
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }
  async function confirmVerify() {
    setBusy(true);
    try {
      await chatApi.confirmCellVerification(af, agent.id, code.trim());
      setVerifying(false); setCode('');
      showToast('Mobile verified — chat alerts will be texted to this number.');
      onChanged();
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function toggleInitialFace(on: boolean) {
    setBusy(true);
    try {
      await chatApi.setInitialFace(af, agent.id, on);
      showToast(on
        ? `${agent.display_name || 'This person'} now appears when the chat opens.`
        : 'Removed as the initial chat face.');
      onChanged();
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function onPhoto(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      await chatApi.uploadPhoto(af, agent.id, file);
      showToast('Photo updated.');
      onChanged();
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
      {agent.photo_url ? (
        <img
          src={agent.photo_url}
          alt=""
          style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{ width: 46, height: 46, borderRadius: '50%', background: '#e2e8f0', display: 'grid', placeItems: 'center', color: '#64748b', fontWeight: 700 }}
        >
          {(agent.display_name ?? '?').slice(0, 1).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontWeight: 600 }}>{agent.display_name || `User #${agent.user_id}`}</div>
        <div style={muted}>
          {[agent.role_sales ? 'Sales' : null, agent.role_recruiting ? 'Recruiting' : null]
            .filter(Boolean)
            .join(' · ') || 'No role'}
          {agent.cell_e164 ? ` · ${agent.cell_e164}` : ''}
          {agent.cell_e164 && !agent.cell_verified_at ? ' (unverified — no texts until verified)' : ''}
          {agent.cell_e164 && agent.cell_verified_at ? ' ✓ texts on' : ''}
        </div>
        {agent.cell_e164 && !agent.cell_verified_at && (
          verifying ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                placeholder="6-digit code"
                style={{ ...input, width: 120 }}
                aria-label="Verification code"
              />
              <button style={linkBtn} disabled={busy || code.trim().length < 6} onClick={confirmVerify}>Confirm</button>
              <button style={linkBtn} disabled={busy} onClick={startVerify}>Resend</button>
            </div>
          ) : (
            <button style={{ ...linkBtn, padding: '4px 0' }} disabled={busy} onClick={startVerify}>Verify mobile by text</button>
          )
        )}
      </div>
      {/* Only one person per franchise can hold this, so ticking it here moves
          it rather than adding a second — the server clears the previous
          holder in the same transaction. Needs a photo to be worth anything. */}
      <label
        style={{ ...rowAction, opacity: busy || !agent.photo_url ? 0.5 : 1 }}
        title={agent.photo_url
          ? 'Show this person’s photo on the chat before anyone joins'
          : 'Add a photo first'}
      >
        <input
          type="checkbox"
          disabled={busy || !agent.photo_url}
          checked={Boolean(agent.is_initial_face)}
          onChange={(e) => toggleInitialFace(e.target.checked)}
          style={{ margin: 0, flex: '0 0 auto' }}
        />
        Appears in initial chat
      </label>
      <label style={{ ...rowAction, opacity: busy ? 0.5 : 1 }}>
        {agent.photo_url ? 'Change photo' : 'Add photo'}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => onPhoto(e.target.files?.[0])}
        />
      </label>
      <button
        style={rowAction}
        onClick={async () => {
          await chatApi.removeAgent(af, agent.id);
          onChanged();
        }}
      >
        Remove
      </button>
    </div>
  );
}

function AddAgent({
  af, users, existing, onAdded,
}: {
  af: string;
  users: Array<{ id: number; name: string | null; email: string | null }>;
  existing: number[];
  onAdded: () => void;
}) {
  const [userId, setUserId] = useState('');
  const [sales, setSales] = useState(true);
  const [recruiting, setRecruiting] = useState(false);
  const [cell, setCell] = useState('');
  const available = users.filter((u) => !existing.includes(u.id));

  async function add() {
    try {
      const picked = users.find((u) => String(u.id) === userId);
      await chatApi.upsertAgent(af, {
        user_id: Number(userId),
        role_sales: sales,
        role_recruiting: recruiting,
        display_name: (picked?.name ?? '').split(' ')[0],
        cell_e164: cell,
      });
      setUserId('');
      setCell('');
      showToast('Added to the chat team.');
      onAdded();
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    }
  }

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Add someone">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} style={{ ...input, minWidth: 220 }}>
            <option value="">Choose a person…</option>
            {available.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
        </Field>
        <Field label="Receives">
          <div style={{ display: 'flex', gap: 12, paddingTop: 6 }}>
            <label style={{ display: 'flex', gap: 6 }}>
              <input type="checkbox" checked={sales} onChange={(e) => setSales(e.target.checked)} /> Sales
            </label>
            <label style={{ display: 'flex', gap: 6 }}>
              <input type="checkbox" checked={recruiting} onChange={(e) => setRecruiting(e.target.checked)} /> Recruiting
            </label>
          </div>
        </Field>
        <Field label="Mobile (for chat alerts)" hint="Optional. Verified before any text is sent.">
          <input value={cell} onChange={(e) => setCell(e.target.value)} placeholder="(555) 555-1234" style={input} />
        </Field>
        <button style={primaryBtn} disabled={!userId} onClick={add}>Add</button>
      </div>
      {available.length === 0 && users.length > 0 && (
        <p style={{ ...muted, marginTop: 8 }}>Everyone at this location is already on the list.</p>
      )}
      {users.length === 0 && (
        <p style={{ ...muted, marginTop: 8 }}>
          No dashboard users are attached to this location yet — staff need a dashboard login before
          they can take chats.
        </p>
      )}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      {hint && <p style={{ ...muted, margin: '0 0 6px' }}>{hint}</p>}
      {children}
    </div>
  );
}

const card: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 8, padding: 18, marginBottom: 18, background: '#fff',
};
const h3: React.CSSProperties = { margin: '0 0 6px', fontSize: 16 };
const muted: React.CSSProperties = { fontSize: 13, color: '#64748b' };
const input: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, fontFamily: 'inherit',
};
const primaryBtn: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 6, border: 'none', background: '#e87722', color: '#fff',
  fontWeight: 600, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', textDecoration: 'underline',
  cursor: 'pointer', fontSize: 13, padding: 4,
};

/**
 * The three controls at the end of an agent row. They must share a line box or
 * their text does not line up: the checkbox is taller than the text beside it,
 * which grew that label's line box and pushed its text 2px below "Change photo"
 * and "Remove". inline-flex with a fixed line-height gives all three the same
 * box whatever they contain.
 */
const rowAction: React.CSSProperties = {
  ...linkBtn,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
};
